/**
 * Full-name (first + last) format validation — shared by frontend and backend so a person's
 * name field is never accepted with a single word on one side and rejected on the other.
 */

export type FullNameError = 'missing_last_name';

/**
 * Validates that a person's full name has at least two whitespace-separated parts (first +
 * last name). Language-agnostic — splits on whitespace only, so it works the same for Hebrew
 * and Latin script. Returns `null` when valid, otherwise the specific error code.
 */
export function validateFullName(name: string): FullNameError | null {
  const tokens = name.trim().split(/\s+/);
  return tokens.length >= 2 ? null : 'missing_last_name';
}

/**
 * A company name is never a "first + last name" — skips the check entirely for a corporate
 * developer/buyer, so backend and frontend schemas can't drift on when this check applies.
 */
export function resolveFullNameValidationError(params: {
  isCorporate: boolean;
  value: string;
}): FullNameError | null {
  if (params.isCorporate) return null;
  return validateFullName(params.value);
}

/** A person's display name from separate first and last names; empty when neither is set. */
export function formatFullName(
  firstName: string | null | undefined,
  lastName: string | null | undefined
): string {
  return [firstName, lastName].filter(Boolean).join(' ').trim();
}

/** Max length of a single first/last name field — shared so the form and API schema agree. */
export const NAME_MAX_LENGTH = 100;

/** Latin letters, with inner spaces, hyphens, apostrophes and dots (e.g. `Mary-Jane O'Neil`). */
const ENGLISH_NAME_PATTERN = /^[A-Za-z]+(?:[ '.-]+[A-Za-z]+)*\.?$/;

/** Whether a (trimmed) name is written in English letters only. */
export function isEnglishName(value: string): boolean {
  return ENGLISH_NAME_PATTERN.test(value.trim());
}
